import * as React from 'react';

import noop from 'lodash/noop';
import {weakMemo} from '@packages/utilities/memo';
import {Plugins} from '@packages/systems/wfdl';

import {emptyObject} from '@packages/utilities/fp/utils';
import {
  styled,
  useTheme,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';

import type {Type} from '@packages/systems/wfdl';
import {usePlugins} from '@packages/utilities/platform';

import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import {
  type TreeViewState,
  type TreeViewProps,
  TreeView,
  getInitialState,
  preselectNode,
  unpreselectNode,
  toggleNode,
  scroll,
  flagNodes,
} from '@packages/systems/design-system/TreeView';

import {FinePrint} from '@packages/systems/design-system/Text';
import OneLiner from '@packages/systems/design-system/OneLiner';
import Grid from '@packages/systems/design-system/Grid';
import {typeToTreeNodes, type TreeNode} from './typeToTreeNodes';
import {TypeTooltip} from './TypeTooltip';
import PillLabel from '@packages/systems/design-system/PillLabel';
import TooltipControl from '@packages/systems/design-system/TooltipControl';

const TREE_VIEW_WIDTH = rightSidebarWidth;

// @ts-expect-error - TS7006 - Parameter 'nodes' implicitly has an 'any' type.
const createRoot = weakMemo((nodes) => ({
  // @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
  getId: (node) => node.id,
  nodes,
}));

const RightmostContainer = emotionStyled('div')({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  color: tokens.colors.textSecondary,
});

const StyledFinePrint = styled(FinePrint)(({theme: {gutters}}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: gutters.x4 / 2,
}));

const StyledPillLabel = styled(PillLabel)(
  ({theme: {colors, fontWeights, gutters}}) => ({
    minWidth: gutters.x12,
    height: gutters.x12,
    background: colors.foregroundDimmer,
    borderStyle: 'none',
    paddingLeft: gutters.x4,
    paddingRight: gutters.x4,
    fontWeight: fontWeights.subheading,
  })
);

// We need a max-width to contrain the the width of the container for the label.
// Otherwise, extremely long labels end up pushing the adjacent container. The below figure has worked in testing
const ONE_LINER_MAX_WIDTH = '160';
const ConstrainedLabel = styled(OneLiner)({
  maxWidth: `${ONE_LINER_MAX_WIDTH}px`,
});

const ExpandedWrapper = emotionStyled('div')({
  width: tokens.space[3],
});

type InfoTip = {
  header: React.ReactNode;
  icon: React.ReactNode;
  content: React.ReactNode;
};

type GetInfoTip = (arg1: Type) => null | InfoTip;

export const TypeView = React.memo<{
  /** The root Type, needed to render the TypeView. */
  type: Type;
  /**
   * The height of the TypeView container. The height determines the
   * scroll behavior.
   */
  height: number;
  /**
   * Optional callback that provides the current Type of the node.
   * Return the InfoTip shape to render an icon with a tip to the
   * right of the node label, or null if none is needed.
   */
  getInfoTip?: GetInfoTip;
}>(function TypeView({type, height, getInfoTip = (_type: Type) => null}) {
  const {gutters, control, colors} = useTheme();
  const TREE_NODE_HEIGHT = control.height;

  const plugins = usePlugins();
  const treeViewNodes = typeToTreeNodes(type, plugins);
  const root = createRoot(treeViewNodes);
  const expandedRootNodes = flagNodes(root)(emptyObject);

  const [state, setState] = React.useState<TreeViewState<'children', TreeNode>>(
    // @ts-expect-error - property root is required by TreeViewState type
    () => ({
      ...getInitialState(),
      expandedNodes: expandedRootNodes,
      width: TREE_VIEW_WIDTH,
      height,
      nodeWidth: TREE_VIEW_WIDTH,
      nodeHeight: TREE_NODE_HEIGHT,
    })
  );

  if (treeViewNodes.length === 0) {
    return null;
  }

  const treeViewProps: TreeViewProps<'children', TreeNode> = {
    ...state,
    root,
    renderNode: ({
      node,
      depth,
      renderIndentation,
      expansionButton,
      isExpanded,
      isExpandable,
    }) => {
      const infoTip = getInfoTip(node.type);
      return (
        <>
          {renderIndentation(depth)}

          <TypeTooltip icon={node.icon} label={node.label} type={node.type}>
            <StyledFinePrint>
              <ExpandedWrapper>{expansionButton}</ExpandedWrapper>
              <Grid
                gridColumnGap={`${gutters.narrow}px`}
                gridRowGap={0}
                alignItems="center"
                gridTemplateColumns={`${gutters.x12}px minmax(0px, max-content)`}
              >
                <Plugins.PluginIcon icon={node.icon} />

                {isExpandable && !isExpanded && node.childrenCount ? (
                  <Grid
                    alignItems="center"
                    gridColumnGap={2}
                    gridTemplateColumns="minmax(0px, max-content) min-content"
                  >
                    <ConstrainedLabel>{node.label}</ConstrainedLabel>
                    <StyledPillLabel labelStyle={{color: colors.foreground}}>
                      {node.childrenCount}
                    </StyledPillLabel>
                  </Grid>
                ) : (
                  <ConstrainedLabel>{node.label}</ConstrainedLabel>
                )}
              </Grid>
            </StyledFinePrint>
          </TypeTooltip>

          {infoTip && (
            <RightmostContainer>
              <TooltipControl
                cursor="default"
                // @ts-expect-error - TS2322 - Type 'ReactNode' is not assignable to type 'Element | undefined'.
                icon={infoTip.icon}
                content={infoTip.content}
                alternate
              />
            </RightmostContainer>
          )}
        </>
      );
    },
    width: TREE_VIEW_WIDTH,
    height,
    nodeWidth: TREE_VIEW_WIDTH, // this will need to be set to max width of widest node for scrolling purposes
    nodeHeight: TREE_NODE_HEIGHT,
    showShadows: false,
    onContextMenu: noop,
    onPreselect: (id) => {
      setState(preselectNode(state, id));
    },
    onUnpreselect: () => {
      setState(unpreselectNode(state));
    },
    onSelect: noop,
    onToggle: (id) => {
      setState(toggleNode(state, id));
    },
    onSortStart: noop,
    onSortChange: noop,
    onSortEnd: noop,
    onScroll: (scrollData) => {
      setState(scroll(state, scrollData));
    },
    onPotentialParentHover: noop,
    onActivate: noop,
    getListItemProps: (theme) => (node: TreeNode) => ({
      'data-automation-id': node.dataAutomationId,
      background: theme.colors.background,
    }),
  };

  return (
    <TreeView
      {...treeViewProps}
      // @ts-expect-error - TS2769 - No overload matches this call.
      enhanceScrollView
      automationId="data-type-view"
    />
  );
});
